import React from 'react';
import { BellRingIcon, CalendarClockIcon, CircleDollarSignIcon, CompassIcon, GraduationCapIcon, MegaphoneIcon } from 'lucide-react';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { Panel } from '../../ui/Panel';
import { ProvenanceBadge } from '../../trust/ProvenanceBadge';
import { useDiscover } from '../../../contexts/DiscoverContext';
import type { AdmissionsUpdate } from '../../../types/admissions';
const KIND_ICONS: Record<AdmissionsUpdate['kind'], React.ComponentType<{
  className?: string;
}>> = {
  Deadline: CalendarClockIcon,
  Scholarship: CircleDollarSignIcon,
  'Open day': GraduationCapIcon,
  Announcement: MegaphoneIcon,
  Funding: CircleDollarSignIcon
};
const KIND_TONES: Record<AdmissionsUpdate['kind'], string> = {
  Deadline: 'bg-danger-50 text-danger-700',
  Scholarship: 'bg-brand-50 text-brand-700',
  'Open day': 'bg-info-50 text-info-700',
  Announcement: 'bg-warn-50 text-warn-800',
  Funding: 'bg-brand-50 text-brand-700'
};
export function FeedSection() {
  const {
    feed,
    setSection
  } = useDiscover();
  return <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_20rem]" data-id="element-1400" data-mp="3maogty05gy75">
      <Panel title="Admissions feed" description="Drawn from the institutions and awards you have saved, and from what your programme suggests." bodyClassName="p-0" data-id="element-1401" data-mp="3m7sii8ke47i6">
        <ul className="divide-y divide-line" data-id="element-1402" data-mp="3dbb857juodnh">
          {feed.map(update => {
          const Icon = KIND_ICONS[update.kind];
          return <li key={update.id} className="flex gap-4 px-5 py-4" data-id="element-1403" data-mp="33c2g4k8jp93r">
                <span aria-hidden className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${KIND_TONES[update.kind]}`} data-id="element-1404" data-mp="2pkfj2i6s3wjx">
                  <Icon className="h-5 w-5" data-id="element-1405" data-mp="2flzrke3ual9w" />
                </span>
                <div className="min-w-0" data-id="element-1406" data-mp="2fnsxfr6mp1gu">
                  <div className="flex flex-wrap items-center gap-2" data-id="element-1407" data-mp="2klcf712jbwdj">
                    <Badge plain data-id="element-1408" data-mp="q4knixm25921">{update.kind}</Badge>
                    <ProvenanceBadge provenance={update.provenance} short data-id="element-1409" data-mp="q5dx06ceicnv" />
                    <span className="text-caption text-ink-subtle" data-id="element-1410" data-mp="ldm4hq9opwv3">{update.time}</span>
                  </div>
                  <p className="mt-2 text-body font-semibold text-ink" data-id="element-1411" data-mp="hdc0uk9d7ije">{update.title}</p>
                  <p className="mt-1 max-w-[70ch] text-body-sm leading-6 text-ink-muted" data-id="element-1412" data-mp="cdz2p3d1mp1i">
                    {update.detail}
                  </p>
                  <p className="mt-1.5 text-caption text-ink-subtle" data-id="element-1413" data-mp="191celt7ucsgn">{update.source}</p>
                </div>
              </li>;
        })}
        </ul>
      </Panel>

      <div className="space-y-5" data-id="element-1414" data-mp="101777j2ndu2h">
        <Panel title="How Campuzon labels admissions data" data-id="element-1415" data-mp="104lbiwuvgdck">
          <ul className="space-y-3 text-body-sm leading-6 text-ink-muted" data-id="element-1416" data-mp="v2w0hh7nfzoh">
            <li data-id="element-1417" data-mp="3kt9beubfusi9">
              <ProvenanceBadge provenance="verified" short data-id="element-1418" data-mp="3ktshndrq7sgg" /> — published by an authorised
              institution representative.
            </li>
            <li data-id="element-1419" data-mp="35pzk0e28fugx">
              <ProvenanceBadge provenance="user" short data-id="element-1420" data-mp="35qhn9unbb8yc" /> — contributed by a student and not yet
              confirmed.
            </li>
            <li data-id="element-1421" data-mp="3apki6m9kvysl">
              <ProvenanceBadge provenance="derived" short data-id="element-1422" data-mp="3apc8124ub7l3" /> — computed from platform activity.
            </li>
            <li data-id="element-1423" data-mp="3fmawp55qimbw">
              <ProvenanceBadge provenance="ai" short data-id="element-1424" data-mp="3kelwm80h684g" /> — generated assistance, always shown with
              its source.
            </li>
          </ul>
        </Panel>

        <Panel title="Assistant" data-id="element-1425" data-mp="3oijwikxmjmyk">
          <p className="flex items-start gap-2.5 text-body-sm leading-6 text-ink-muted" data-id="element-1426" data-mp="3tfac775iogjg">
            <CompassIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-1427" data-mp="2996le4bmf57" />
            Ask “which of my saved universities have applications closing this month?” from the
            Assistant in the top bar. It answers from published programme data only, and refuses to
            invent a requirement, deadline, fee or award.
          </p>
        </Panel>

        <Panel title="Next step" data-id="element-1428" data-mp="291t8d76w81t5">
          <p className="flex items-start gap-2.5 text-body-sm leading-6 text-ink-muted" data-id="element-1429" data-mp="3b6fe3a9pham">
            <BellRingIcon aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-ink-subtle" data-id="element-1430" data-mp="3uhpu3tj85re7" />
            Your TU Delft application is missing an IELTS result and a motivation letter.
          </p>
          <Button size="sm" variant="secondary" className="mt-3" onClick={() => setSection('Applications')} data-id="element-1431" data-mp="7d09fspyjn1t">
            Open application workspace
          </Button>
        </Panel>
      </div>
    </div>;
}